import { BackgroundPattern } from 'types/pageTypes';
import { isLight } from 'utils/color';

import { DEFAULT_PATTERNS, DEFAULT_PATTERNS_DARK } from './PatternMenu.config';

/**
 * Retrieves the background pattern URL based on the provided pattern name and background type.
 *
 * @param {string} patternName - The name of the pattern to retrieve.
 * @param {boolean} [isDarkBg=false] - Specifies whether the background is dark or not. Default is false (light background).
 * @returns {string|undefined} The URL of the background pattern, or undefined if the pattern name is unknown.
 */
const getBackgroundPattern = (patternName?: string, isDarkBg?: boolean) => {
  if (!patternName) return undefined;
  return isDarkBg ? DEFAULT_PATTERNS_DARK[patternName] : DEFAULT_PATTERNS[patternName];
};

/**
 * Switch to light or dark pattern, based on an existing pattern and background.
 *
 * @param {BackgroundPattern} [existingPattern] - The existing background pattern.
 * @param {string} [newColor] - The new color for the pattern.
 * @returns {BackgroundPattern|undefined} A new background pattern with the updated color, or undefined if inputs are missing or the pattern could not be regenerated.
 */
const switchPattern = (existingPattern?: BackgroundPattern, newColor?: string): BackgroundPattern | undefined => {
  if (!existingPattern || !newColor) {
    return undefined;
  }

  const isDarkBg = !isLight(newColor);
  const svgPattern = getBackgroundPattern(existingPattern?.id, isDarkBg);
  return svgPattern
    ? {
        id: existingPattern?.id || '',
        svgPattern,
      }
    : existingPattern;
};

export { getBackgroundPattern, switchPattern };
